import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ReactECharts from 'echarts-for-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';

const { FiActivity, FiTrendingUp, FiTarget, FiPieChart } = FiIcons;

const Analytics = () => {
  const { session } = useAuth();
  const [analyticsData, setAnalyticsData] = useState({ months: [], atsSuccessRate: [], interviewRate: [] });

  useEffect(() => {
    const fetchAndProcessData = async () => {
      if (!session?.user?.id) return;

      const { data: applications } = await supabase
        .from('applications')
        .select('*')
        .eq('profile_id', session.user.id)
        .order('created_at', { ascending: true });

      if (!applications || applications.length === 0) {
        setAnalyticsData({
          months: ['No Data'],
          atsSuccessRate: [0],
          interviewRate: [0]
        });
        return;
      }

      // Group by month
      const groupedData = {};
      applications.forEach(app => {
        const date = new Date(app.created_at);
        const monthYear = date.toLocaleString('default', { month: 'short', year: '2-digit' });

        if (!groupedData[monthYear]) {
          groupedData[monthYear] = { total: 0, matches: 0, interviewing: 0 };
        }

        groupedData[monthYear].total += 1;
        groupedData[monthYear].matches += (app.match_score || 0);
        if (app.status === 'Interviewing' || app.status === 'Offer') {
          groupedData[monthYear].interviewing += 1;
        }
      });

      const months = Object.keys(groupedData);
      const atsSuccessRate = months.map(m => Math.round(groupedData[m].matches / groupedData[m].total));
      const interviewRate = months.map(m => Math.round((groupedData[m].interviewing / groupedData[m].total) * 100));

      setAnalyticsData({ months, atsSuccessRate, interviewRate });
    };

    fetchAndProcessData();
  }, [session]);

  const mainChartOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: ['ATS Match Rate', 'Interview Invite Rate'], bottom: 0 },
    xAxis: { type: 'category', data: analyticsData.months, axisLine: { show: false } },
    yAxis: { type: 'value', axisLabel: { formatter: '{value}%' }, splitLine: { lineStyle: { type: 'dashed' } } },
    series: [
      { name: 'ATS Match Rate', data: analyticsData.atsSuccessRate, type: 'line', smooth: true, itemStyle: { color: '#3b82f6' }, areaStyle: { color: 'rgba(59, 130, 246, 0.1)' } },
      { name: 'Interview Invite Rate', data: analyticsData.interviewRate, type: 'line', smooth: true, itemStyle: { color: '#10b981' }, areaStyle: { color: 'rgba(16, 185, 129, 0.1)' } }
    ],
    grid: { left: '3%', right: '4%', bottom: '15%', containLabel: true }
  };

  const distributionOption = {
    series: [{
      type: 'pie',
      radius: ['40%', '70%'],
      avoidLabelOverlap: false,
      itemStyle: { borderRadius: 10, borderColor: '#fff', borderWidth: 2 },
      label: { show: false },
      data: [
        { value: 45, name: 'Direct Applications', itemStyle: { color: '#3b82f6' } },
        { value: 25, name: 'Referrals', itemStyle: { color: '#8b5cf6' } },
        { value: 20, name: 'Recruiter Reach-out', itemStyle: { color: '#f59e0b' } },
        { value: 10, name: 'Other', itemStyle: { color: '#9ca3af' } }
      ]
    }]
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">ATS Performance Analytics</h1>
        <p className="text-gray-500 mt-1">Deep dive into your application funnel and success metrics.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
            <SafeIcon icon={FiActivity} className="text-blue-500" /> Success Funnel Over Time
          </h3>
          <ReactECharts option={mainChartOption} style={{ height: '400px' }} />
        </div>

        <div className="space-y-8">
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
            <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
              <SafeIcon icon={FiPieChart} className="text-blue-500" /> Application Sources
            </h3>
            <ReactECharts option={distributionOption} style={{ height: '240px' }} />
            <div className="space-y-2 mt-4">
              <div className="flex justify-between text-xs">
                <span className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-blue-500"></div> Direct</span>
                <span className="font-bold">45%</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-purple-500"></div> Referrals</span>
                <span className="font-bold">25%</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-600 to-blue-800 p-6 rounded-2xl text-white shadow-xl">
            <h3 className="font-bold mb-2">Pro Insight</h3>
            <p className="text-sm text-blue-100 leading-relaxed">Your "Interview Invite Rate" jumped 15% after you started using the AI Bullet Transformation tool in August.</p>
            <button className="mt-4 w-full py-2 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-bold transition-colors">
              Download Full Report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;